import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { plainToClass } from 'class-transformer';

import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { Agreement } from 'app/entities/transaction-profile/agreement.model';
import { Document } from 'app/shared/models/document.model';
import { Right } from 'app/entities/transaction-profile/right.model';
import { Risk, RiskControl } from 'app/entities/transaction-profile/risk.model';
import { TransactionRule } from 'app/entities/transaction-profile/right.model';
import { Obligation } from 'app/entities/transaction-profile/obligation.model';

import { CookieService } from './../../services/cookie.service';

import { BankAccount } from 'app/shared/models/bank-account.model';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';
/**
 * Service for entity monitoring tab.
 */
@Injectable()
export class EntityTransactionProfileService {
    entityDetailsUpdated: EventEmitter<EntityIdDetails> = new EventEmitter();
    selectedAgreement: EventEmitter<Agreement> = new EventEmitter();

    private entityIdDetails: EntityIdDetails;

    constructor(private requestService: ObservableRequestService,
        private cookieService: CookieService) {
        this.entityIdDetails = new EntityIdDetails();
    }

    setEntityIdDetails(entityIdDetails: EntityIdDetails): void {
        this.entityIdDetails = entityIdDetails;
        this.entityDetailsUpdated.emit(this.entityIdDetails);
    }

    getEntityIdDetails(): EntityIdDetails {
        return this.entityIdDetails;
    }

    getDateForPrint(rawDate) {
        return formatDisplayDate(rawDate);
    }

    getAgreements(entityId: number) {
        return this.requestService.getRequest(`${ConstantsService.AGREEMENTS}/${entityId}`)
            .map(response => {
                return plainToClass(Agreement, response.arrResult as Object[]);
            });
    }

    saveAgreement(agreement: Agreement) {
        const saveAgreementUrl = ConstantsService.AGREEMENTS;
        return this.requestService.putRequest(saveAgreementUrl,
            JSON.stringify(agreement),
            this.requestService.getJSONContentType());
    }

    getDocuments(entityId: number) {
        return this.requestService.getRequest(`${ConstantsService.AGREEMENTS}/${entityId}`)
            .map(response => {
                return plainToClass(Agreement, response.arrResult as Object[]);
            });
    }

    getDocumentsByAgreementId(agreementId: number) {
        return this.requestService.getRequest(`${ConstantsService.AGREEMENTS}/${agreementId}/documents`)
            .map(response => {
                return plainToClass(Document, response.arrResult as Object[]);
            });
    }

    saveAgreementDocument(agreementId, documentIds) {
        return this.requestService.putRequest(
            `${ConstantsService.AGREEMENTS}/${agreementId}/documents`,
            JSON.stringify(documentIds),
            this.requestService.getJSONContentType());

    }

    getTransactionRulesByClientId(clientId: number) {
      return this.requestService.getRequest(`${ConstantsService.GET_TRANSACTION_RULES}/${clientId}`)
        .map(response => {
          return plainToClass(TransactionRule, response.arrResult as Object[]);
        });
    }

    getTransactionRuleHistory(ruleId: number) {
      return this.requestService.getRequest(`${ConstantsService.TRANSACTION_RULE}/${ruleId}/history`)
        .map(response => {
          return plainToClass(TransactionRule, response.arrResult as Object[]);
        });
    }

    restoreTransactionRuleVersion(clientId: number, historicalRuleId: number) {
      return this.requestService.postRequest(
        `/transactionprofile/client/${clientId}/txnrule/${historicalRuleId}/restore`,
        null,
        this.requestService.getJSONContentType()
      ).map(response => {
        return response.arrResult;
      });
    }

    getRights(agreementId: number) {
        return this.requestService.getRequest(`${ConstantsService.RIGHTS}/${agreementId}`)
            .map(response => {
                return plainToClass(Right, response.arrResult as Object[]);
            });
    }

    /**
     * Gets all transaction rules (both Rights and Obligations) for an agreement in a single call.
     * This is more efficient than calling getRights and getObligations separately.
     * 
     * @param agreementId The agreement ID
     * @return Observable containing both Rights and Obligations
     */
    getAllTransactionRules(agreementId: number) {
        return this.requestService.getRequest(`${ConstantsService.ALL_TRANSACTION_RULES}/${agreementId}/transaction-rules`)
            .map(response => {
                const data = response.arrResult;
                const rights = plainToClass(Right, (data.rights || []) as Object[]);
                const obligations = plainToClass(Obligation, (data.obligations || []) as Object[]);
                return { rights, obligations };
            });
    }

    getRisks(agreementId: any) {
        return this.requestService.getRequest(`${ConstantsService.RISKS}/${agreementId}`)
            .map(response => {
                return plainToClass(Risk, response.arrResult as Object[]);
            });
    }

    getObligations(agreementId: number) {
        return this.requestService.getRequest(`${ConstantsService.OBLIGATIONS}/${agreementId}`)
            .map(response => {
                return plainToClass(Obligation, response.arrResult as Object[]);
            });
    }

    setSelectedAgreement(selectedAgreement: Agreement) {
        console.log('EntityTransactionProfileService.setSelectedAgreement called with:', selectedAgreement);
        this.selectedAgreement.emit(selectedAgreement);
        console.log('Event emitted to selectedAgreement subscribers');
    }

    saveRisk(agreementId: number, risk: Risk) {
        return this.requestService.putRequest(`${ConstantsService.RISKS}/${agreementId}`,
            JSON.stringify(risk),
            this.requestService.getJSONContentType());
    }

    addRisk(risk: Risk) {
        return this.requestService.postRequest(`${ConstantsService.RISKS}`,
            JSON.stringify(risk),
            this.requestService.getJSONContentType());
    }

    updateRisk(risk: Risk) {
        return this.requestService.putRequest(`${ConstantsService.RISKS}`,
            JSON.stringify(risk),
            this.requestService.getJSONContentType());
    }

    saveRight(right: Right) {
        return this.requestService.putRequest(ConstantsService.RIGHTS,
            JSON.stringify(right),
            this.requestService.getJSONContentType());
    }

    saveObligation(obligation: Obligation) {
        return this.requestService.putRequest(ConstantsService.OBLIGATIONS,
            JSON.stringify(obligation),
            this.requestService.getJSONContentType());
    }

    saveTransactionRule(clientId: number, obligationId: number, transactionRule: TransactionRule) {
        const saveTransactionRuleUrl = `${ConstantsService.ADD_TRANSACTION_RULE}/${clientId}/right/${obligationId}/txnrule`;
        return this.requestService.putRequest(saveTransactionRuleUrl,
            JSON.stringify(transactionRule),
            this.requestService.getJSONContentType());
    }

    updateTransactionRuleStatus(clientId: number, transactionRule: TransactionRule) {
        const saveTransactionRuleUrl = `${ConstantsService.ADD_TRANSACTION_RULE}/${clientId}/txnrule`;
        return this.requestService.putRequest(saveTransactionRuleUrl,
            JSON.stringify(transactionRule),
            this.requestService.getJSONContentType());
    }

    removeRisk(riskId: number) {
        return this.requestService.deleteRequest(`${ConstantsService.RISKS}/${riskId}`, null,
            this.requestService.getJSONContentType());
    }

    removeAgreement(agreement: Agreement): Observable<any> {
        return this.requestService.deleteRequest(ConstantsService.AGREEMENTS + '/' + agreement.id,
            null, this.requestService.getJSONContentType());
    }

    removeDocument(document: Document, agreementId: number): Observable<any> {
        return this.requestService.deleteRequest(
            ConstantsService.AGREEMENT_DOCUMENTS + '/' + agreementId + ConstantsService.DOCUMENTS + '/' + document.id,
            null, this.requestService.getJSONContentType());
    }

    uploadDocumentForAgreement(files, entityId, agreementId): XMLHttpRequest {
        const formData: FormData = new FormData();

        for (let i = 0; i < files.length; i++) {
            formData.append('files', files.item(i), files.item(i).name);
        }
        formData.append('entityId', entityId);

        let url = ConstantsService.APP_BE_URL + '/rest' + ConstantsService.AGREEMENT_DOCUMENT_UPLOAD;
        url = url + '/' + agreementId + '/documentsupload';
        const xhr: XMLHttpRequest = new XMLHttpRequest();

        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Authorization',
            'Bearer ' + this.cookieService.get(ConstantsService.COOKIE_ACCESS_TOKEN));
        xhr.setRequestHeader('app-type', 'TRUST');
        xhr.send(formData);
        return xhr;
    }
    getBankAccountsForEntity(entityId: number): Observable<any> {
        const clientAccountsURL = `${ConstantsService.BANK_ACCOUNTS}/${entityId}`;
        return this.requestService.getRequest(clientAccountsURL)
            .map(response => {
                return plainToClass(BankAccount, response.arrResult as Object[]);
            });
    };

    getRiskControls(riskId: number) {
        const riskControlURL = `${ConstantsService.RISKS}/${riskId}/riskcontrol`;
        return this.requestService.getRequest(riskControlURL)
            .map(response => {
                return plainToClass(RiskControl, response.arrResult as Object[]);
            });
    }

    addRiskControl(riskId: number, riskControl: RiskControl) {
        const riskControlURL = `${ConstantsService.RISKS}/${riskId}/riskcontrol`;
        return this.requestService.postRequest(riskControlURL,
            JSON.stringify(riskControl),
            this.requestService.getJSONContentType());
    }

    deleteRiskControl(riskControlId: number) {
        const riskControlURL = `${ConstantsService.RISKS}/riskcontrol/${riskControlId}`;
        return this.requestService.deleteRequest(riskControlURL, null,
            this.requestService.getJSONContentType());
    }

    getTransactionRules(clientId: number, rightId: number, isRight: boolean): Observable<any> {
        const type = isRight && 'right' || 'obligation';
        const getTransactionRuleUrl = `${ConstantsService.GET_TRANSACTION_RULE}/${clientId}/${type}/${rightId}/txnrule`;
        return this.requestService.getRequest(getTransactionRuleUrl)
            .map(response => {
                return plainToClass(TransactionRule, response.arrResult as Object[]);
            });
    };

    getTransactionCounterParty(entityId: number, limit?: number): Observable<any> {
        // Backend @QueryParam("limit") is an Integer. A missing/invalid limit
        // used to send the literal "totalRecords" and Jersey returned 404.
        const pageSize = limit != null && limit > 0 ? limit : 10000;
        const clientTransactionCounterURL =
            `${ConstantsService.TRANSACTION_COUNTER_PARTY}/${entityId}?offset=0&limit=${pageSize}`;
        return this.requestService.getRequest(clientTransactionCounterURL);
    };

    downloadReport(url: string): Observable<Blob> {
        return this.requestService.downloadFile(url);
    }

    downloadClientProfileExport(entityId: number): Observable<{ blob: Blob, fileName: string }> {
        const url = `${ConstantsService.TRANSACTION_PROFILE_CLIENT_EXPORT_ENDPOINT}/${entityId}`;
        return this.requestService.downloadFileWithName(url, `Annex_27_Transaction_Profile_${entityId}.xlsx`);
    }

    removeTransactionRule(txnRuleId: number): Observable<any> {
        return this.requestService.deleteRequest(`${ConstantsService.TRANSACTION_RULE}/${txnRuleId}`,
            null, this.requestService.getJSONContentType());
    }

    saveTransactionRuleForRight(clientId: number, rightId: number, transactionRule: TransactionRule) {
        const saveTransactionRuleUrl = `${ConstantsService.ADD_TRANSACTION_RULE}/${clientId}/right/${rightId}/txnrule`;
        return this.requestService.putRequest(saveTransactionRuleUrl,
            JSON.stringify(transactionRule),
            this.requestService.getJSONContentType());
    }

    removeRight(rightId: number) {
        return this.requestService.deleteRequest(`${ConstantsService.RIGHTS}/${rightId}`, null,
            this.requestService.getJSONContentType());
    }

    removeObligation(obligationId: number) {
        return this.requestService.deleteRequest(`${ConstantsService.OBLIGATIONS}/${obligationId}`, null,
            this.requestService.getJSONContentType());
    }

}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/entity-transaction-profile.service.ts